import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { QueuesService } from '../../../service/queues.service';
import { MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { ResolveByDateAndLimitPayload } from '../../../model/queue.model';
import { DialogResult } from '../../../enum/dialog.enum';
import { DateUtils } from '@cv-invision/core';

export type ResolveMode = 'reference' | 'dates';

export interface ResolveDialogPayload {
    mode: 'byReference' | 'byDates';
    referenceId?: string;
    limit?: number;
    startDate?: string; // ISO yyyy-mm-dd
    endDate?: string; // ISO yyyy-mm-dd
}

@Component({
    selector: 'cv-queue-resolve-dialog',
    templateUrl: 'resolve-dialog.component.html',
    styleUrls: ['resolve-dialog.component.scss'],
})
export class ResolveDialogComponent implements OnInit {
    // dropdown options
    readonly modeOptions = [
        { label: 'By Reference Id', value: 'reference' as ResolveMode },
        { label: 'By Dates', value: 'dates' as ResolveMode },
    ];

    resolveForm: FormGroup = this.fb.group({
        mode: ['reference' as ResolveMode], // dropdown decides which section is active
        referenceId: [''],

        limit: [null],
        startDate: [null as Date | null],
        endDate: [null as Date | null],
    });

    constructor(
        private queueService: QueuesService,
        private fb: FormBuilder,
        private messagesService: MessageService,
        private dialogRef: DynamicDialogRef,
    ) {}

    ngOnInit(): void {
        // apply validators whenever mode changes
        this.modeCtrl.valueChanges.subscribe(() => this.applyValidators());
        this.applyValidators();
    }

    // --- convenience getters ---
    get modeCtrl(): AbstractControl {
        return this.resolveForm.get('mode')!;
    }
    get isReference(): boolean {
        return this.modeCtrl.value === 'reference';
    }
    get isDates(): boolean {
        return this.modeCtrl.value === 'dates';
    }

    get canResolve(): boolean {
        if (this.isReference) return this.resolveForm.get('referenceId')!.valid;
        if (this.isDates) {
            const limitOk = this.resolveForm.get('limit')!.valid;
            const s = this.resolveForm.get('startDate')!.value as Date | null;
            const e = this.resolveForm.get('endDate')!.value as Date | null;
            const datesOk = !s || !e || s <= e;
            return limitOk && datesOk;
        }
        return false;
    }

    showErrors(controlName: string): boolean {
        const c = this.resolveForm.get(controlName);
        return !!(c && c.invalid && (c.dirty || c.touched));
    }

    private applyValidators(): void {
        const referenceId = this.resolveForm.get('referenceId')!;
        const limit = this.resolveForm.get('limit')!;
        const start = this.resolveForm.get('startDate')!;
        const end = this.resolveForm.get('endDate')!;

        // Clear validators
        referenceId.clearValidators();
        limit.clearValidators();

        if (this.isReference) {
            // Enable reference controls, disable date controls
            referenceId.enable({ emitEvent: false });
            limit.disable({ emitEvent: false });
            start.disable({ emitEvent: false });
            end.disable({ emitEvent: false });

            referenceId.setValidators([Validators.required, Validators.maxLength(256)]);
        } else {
            // Enable date controls, disable reference controls
            referenceId.disable({ emitEvent: false });
            limit.enable({ emitEvent: false });
            start.enable({ emitEvent: false });
            end.enable({ emitEvent: false });

            limit.setValidators([Validators.required, Validators.min(1)]);
        }

        referenceId.updateValueAndValidity({ emitEvent: false });
        limit.updateValueAndValidity({ emitEvent: false });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    async resolve(): Promise<void> {
        if (!this.canResolve) return;

        if (this.isReference) {
            const referenceId = String(this.resolveForm.get('referenceId')!.value || '').trim();
            await this.resolveByReferenceId(referenceId);
        }

        if (this.isDates) {
            const limit = Number(this.resolveForm.get('limit')!.value);
            const start = new Date(this.resolveForm.get('startDate')!.value);
            const end = new Date(this.resolveForm.get('endDate')!.value);
            await this.resolveByDatesAndLimit({limit, start, end});
        }
    }

    /**
     * Resolves the queues by dates and limits.
     * @param payload 
     */
    async resolveByDatesAndLimit(payload: { limit: number; start: Date; end: Date; }) {
        try {
            const payloadResolve: ResolveByDateAndLimitPayload = {
                limit: payload.limit ?? 1,
                startDate:  DateUtils.formatDateForApi(payload.start) ?? '',
                endDate: DateUtils.formatDateForApi(payload.end, true) ?? '',
                maxParallelism: 1
            }
            await lastValueFrom(this.queueService.resolveQueuesByLimitAndDate(payloadResolve));
            this.closeDialogSuccessfully();
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Close the dialog successfully;
     */
    closeDialogSuccessfully() {
        this.messagesService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'The resolve process was successfully started, this might take a couple minutes.',
        });
        this.dialogRef.close(DialogResult.SUCCESS);
    }

    /**
     * Resolves the queues by reference Id.
     * @param referenceId 
     */
    async resolveByReferenceId(referenceId: string) {
        try {
            await lastValueFrom(this.queueService.resolveQueuesByExternalId(referenceId));
            this.closeDialogSuccessfully();
        } catch (error) {
            console.error(error)
        }
    }
}
